import { useEffect, useRef, useState } from "react";
import { Search, X, ChevronUp } from "lucide-react";
import { sortEvents } from "@/lib/themes";

export default function TimeNavigator({ timeline, t, q, setQ, category, setCategory }) {
  const [progress, setProgress] = useState(0);
  const [open, setOpen] = useState(true);
  const dragging = useRef(false);
  const trackRef = useRef(null);

  const events = sortEvents(timeline?.events || []);
  const years = events.map((e) => Number(e.year) || 0);
  const min = years.length ? Math.min(...years) : 0;
  const max = years.length ? Math.max(...years) : 1;
  const span = max - min || 1;
  const categories = ["All", ...Array.from(new Set(events.map((e) => e.category).filter(Boolean)))];

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? window.scrollY / h : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrubTo = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const h = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo({ top: ratio * h, behavior: "auto" });
  };

  useEffect(() => {
    const move = (e) => { if (dragging.current) scrubTo(e.touches ? e.touches[0].clientX : e.clientX); };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, []);

  const jump = (id) => document.getElementById(`ev-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[min(760px,calc(100%-2rem))]" data-testid="time-navigator">
      <div className="glass rounded-2xl p-3 sm:p-4" style={{ color: t.text }}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="nav-search"
              placeholder="Search events…"
              className="w-full h-9 pl-9 pr-8 rounded-full bg-transparent border text-sm outline-none"
              style={{ borderColor: t.surfaceBorder, color: t.text }} />
            {q && <button onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 opacity-60"><X size={14} /></button>}
          </div>
          <button onClick={() => setOpen((o) => !o)} className="h-9 w-9 grid place-items-center rounded-full border shrink-0" style={{ borderColor: t.surfaceBorder }}>
            <ChevronUp size={16} className={`transition-transform ${open ? "" : "rotate-180"}`} />
          </button>
        </div>

        {open && (
          <>
            {categories.length > 1 && (
              <div className="flex gap-1.5 mt-3 overflow-x-auto no-scrollbar">
                {categories.map((c) => (
                  <button key={c} onClick={() => setCategory(c)} data-testid={`nav-cat-${c}`}
                    className="text-xs px-3 py-1 rounded-full whitespace-nowrap transition-colors border"
                    style={category === c
                      ? { background: t.accent, color: t.dark ? "#0b0616" : "#fff", borderColor: t.accent }
                      : { borderColor: t.surfaceBorder, color: t.muted }}>{c}</button>
                ))}
              </div>
            )}

            <div className="mt-4 px-1">
              <div className="flex items-center justify-between text-[10px] font-semibold tracking-widest opacity-60 mb-1.5">
                <span>{events[0]?.date}</span><span>{events[events.length - 1]?.date}</span>
              </div>
              <div ref={trackRef} className="relative h-8 cursor-pointer"
                onMouseDown={(e) => { dragging.current = true; scrubTo(e.clientX); }}
                onTouchStart={(e) => { dragging.current = true; scrubTo(e.touches[0].clientX); }}>
                <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] rounded-full" style={{ background: t.line }} />
                {events.map((e) => {
                  const pos = ((Number(e.year) || 0) - min) / span;
                  return (
                    <button key={e.id} onClick={(ev) => { ev.stopPropagation(); jump(e.id); }} data-testid={`nav-tick-${e.id}`}
                      title={`${e.date} — ${e.title}`}
                      className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-2.5 w-2.5 rounded-full transition-transform hover:scale-150"
                      style={{ left: `${pos * 100}%`, background: e.color || t.dot }} />
                  );
                })}
                <div className="absolute top-0 bottom-0 w-[3px] rounded-full pointer-events-none" style={{ left: `${progress * 100}%`, background: t.text, opacity: 0.85 }} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
